import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog, Lock, CreditCard, Trash2, Sparkles, Image as ImageIcon, Upload, X, CalendarClock, FileText, Download, ExternalLink, Settings, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { confirmAction } from "@/lib/confirm";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import CancelSubscriptionModal from "./CancelSubscriptionModal";

export default function ProfileModal({ open, onOpenChange, onUpgradeClick }) {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState("account");
  const [info, setInfo] = useState({ name: "", email: "" });
  const [savingInfo, setSavingInfo] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  // Subscription (billing tab)
  const [sub, setSub] = useState(null);
  const [loadingSub, setLoadingSub] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  // AI credit used (account tab)
  const [aiUsage, setAiUsage] = useState(null);
  const [aiUsageError, setAiUsageError] = useState(false);

  // Password
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);

  // Close account
  const [closePwd, setClosePwd] = useState("");
  const [closeConfirm, setCloseConfirm] = useState("");
  const [closing, setClosing] = useState(false);

  // Branding
  const [companyName, setCompanyName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (user && open) {
      setInfo({ name: user.name || "", email: user.email || "" });
      setCompanyName(user.company_name || "");
      setLogoUrl(user.custom_logo_url || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    // Reset tab + transient state only when the modal opens
    if (open) {
      setTab("account");
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
      setClosePwd("");
      setCloseConfirm("");
    }
  }, [open]);

  // Keep company_name/logoUrl in sync when the user object refreshes (after save)
  useEffect(() => {
    if (user) {
      setCompanyName(user.company_name || "");
      setLogoUrl(user.custom_logo_url || "");
    }
  }, [user?.company_name, user?.custom_logo_url]);

  // Fetch subscription info + invoices whenever the billing tab is active for a pro user
  useEffect(() => {
    if (!open || tab !== "billing" || !user?.is_pro) return;
    let cancelled = false;
    (async () => {
      setLoadingSub(true);
      setLoadingInvoices(true);
      try {
        const [subRes, invRes] = await Promise.all([
          api.get("/billing/subscription"),
          api.get("/billing/invoices"),
        ]);
        if (!cancelled) {
          setSub(subRes.data);
          setInvoices(invRes.data?.invoices || []);
        }
      } catch (err) {
        // Non-fatal — UI shows the billing area empty and the user can retry via the "Manage" button.
        console.warn("ProfileModal: subscription/invoices load failed:", err?.message || err);
      } finally {
        if (!cancelled) {
          setLoadingSub(false);
          setLoadingInvoices(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tab, user?.is_pro]);

  // Fetch the AI credit used whenever the account tab is active
  useEffect(() => {
    if (!open || tab !== "account") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/ai/usage");
        if (!cancelled) {
          setAiUsage(data);
          setAiUsageError(false);
        }
      } catch (err) {
        console.warn("ProfileModal: AI usage load failed:", err?.message || err);
        if (!cancelled) setAiUsageError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tab]);

  const formatEur = (amount) =>
    new Intl.NumberFormat(i18n.language || undefined, { style: "currency", currency: "EUR" }).format(amount || 0);

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const { data } = await api.post("/billing/reactivate-subscription");
      setSub(data);
      toast.success(t("profile.reactivated"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setReactivating(false);
    }
  };

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.origin + "/dashboard",
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error(t("profile.portalError"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("profile.portalError"));
    } finally {
      setOpeningPortal(false);
    }
  };

  const formatSubDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString(i18n.language || undefined, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return iso.slice(0, 10);
    }
  };

  const handleSaveInfo = async (e) => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      const { data } = await api.patch("/auth/me", info);
      setUser(data);
      if (data.pending_email && data.pending_email.toLowerCase() === info.email.toLowerCase()) {
        // The address only changes once the link sent to it is clicked
        setInfo((cur) => ({ ...cur, email: data.email }));
        toast.success(t("profile.emailPending", { email: data.pending_email }));
      } else {
        toast.success(t("profile.updated"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSavingInfo(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp", "image/gif"].includes(file.type)) {
      toast.error(t("profile.logoTypeError"));
      return;
    }
    if (file.size > 2_000_000) {
      toast.error(t("profile.logoTooLarge"));
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.url);
      toast.success(t("profile.logoUploaded"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleSaveBrand = async (e) => {
    e.preventDefault();
    setSavingBrand(true);
    try {
      const payload = {
        company_name: companyName,
        custom_logo_url: logoUrl || "",
      };
      const { data } = await api.patch("/auth/me", payload);
      setUser(data);
      toast.success(t("profile.brandingSaved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSavingBrand(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoUrl("");
    try {
      const { data } = await api.patch("/auth/me", { custom_logo_url: "" });
      setUser(data);
      toast.success(t("profile.logoRemoved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    }
  };

  const handleChangePwd = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) {
      toast.error(t("profile.pwdMismatch"));
      return;
    }
    if (newPwd.length < 12) {
      toast.error(t("profile.pwdTooShort"));
      return;
    }
    setChangingPwd(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: currentPwd || undefined,
        new_password: newPwd,
      });
      // Every other session was revoked: keep this one with the fresh token
      if (data?.token) localStorage.setItem("gantt_token", data.token);
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
      toast.success(t("profile.pwdUpdated"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setChangingPwd(false);
    }
  };

  const handleLogoutAll = async () => {
    setLoggingOutAll(true);
    try {
      const { data } = await api.post("/auth/logout-all");
      if (data?.token) localStorage.setItem("gantt_token", data.token);
      toast.success(t("profile.logoutAllDone"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setLoggingOutAll(false);
    }
  };

  const DELETE_KEYWORD = t("profile.deleteKeyword");

  const handleCloseAccount = async (e) => {
    e.preventDefault();
    if (closeConfirm !== DELETE_KEYWORD) {
      toast.error(t("profile.typeDeleteToast"));
      return;
    }
    if (!(await confirmAction({ message: t("profile.closeConfirmFinal"), destructive: true }))) return;
    setClosing(true);
    try {
      await api.post("/auth/close-account", {
        password: closePwd || undefined,
        confirm: "SUPPRIMER",
      });
      toast.success(t("profile.accountDeleted"));
      await logout();
      onOpenChange(false);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
      setClosing(false);
    }
  };

  const TABS = [
    { k: "account", label: t("profile.tabAccount"), icon: UserCog },
    { k: "branding", label: t("profile.tabBranding"), icon: ImageIcon },
    { k: "password", label: t("profile.tabPassword"), icon: Lock },
    { k: "billing", label: t("profile.tabBilling"), icon: CreditCard },
    { k: "privacy", label: t("gdpr.title"), icon: ShieldCheck },
    { k: "danger", label: t("profile.tabDanger"), icon: Trash2 },
  ];

  const [exporting, setExporting] = useState(false);
  const handleExportGdpr = async () => {
    setExporting(true);
    try {
      const res = await api.get("/gdpr/export", { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const fname = match ? match[1] : `flowgantt-export-${Date.now()}.json`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("gdpr.exported"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] min-h-[420px]">
          {/* Sidebar */}
          <div className="border-b border-stone-200 bg-stone-50 p-4 pb-3 md:border-b-0 md:border-r md:pb-4">
            <DialogHeader className="mb-3 md:mb-6">
              <DialogTitle className="font-display text-lg font-black tracking-tight">
                {t("profile.title")}
              </DialogTitle>
            </DialogHeader>
            {/* Phones: swipeable tab strip. md+: the classic vertical sidebar. */}
            <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 md:mx-0 md:block md:space-y-1 md:overflow-visible md:px-0">
              {TABS.map((tab_) => (
                <button
                  key={tab_.k}
                  data-testid={`profile-tab-${tab_.k}`}
                  onClick={() => setTab(tab_.k)}
                  className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-sm text-left transition-colors md:w-full md:rounded-sm md:px-3 ${
                    tab === tab_.k
                      ? tab_.k === "danger"
                        ? "bg-red-50 text-red-600 font-semibold"
                        : "bg-neutral-950 text-white font-semibold"
                      : tab_.k === "danger"
                      ? "text-red-500 hover:bg-red-50"
                      : "text-neutral-700 hover:bg-white"
                  }`}
                >
                  <tab_.icon className="w-4 h-4" strokeWidth={1.75} />
                  {tab_.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-5 md:p-8">
            {tab === "account" && (
              <form onSubmit={handleSaveInfo} className="space-y-4 max-w-md">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mb-2">
                    {t("profile.sectionInfo")}
                  </div>
                  <div className="font-display text-2xl font-black tracking-tighter">
                    {t("profile.yourInfo")}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em]">{t("profile.fullName")}</Label>
                  <Input
                    data-testid="profile-name-input"
                    value={info.name}
                    onChange={(e) => setInfo({ ...info, name: e.target.value })}
                    required
                    className="mt-2 h-11 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em]">{t("profile.email")}</Label>
                  <Input
                    type="email"
                    data-testid="profile-email-input"
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={info.email}
                    onChange={(e) => setInfo({ ...info, email: e.target.value })}
                    required
                    className="mt-2 h-11 rounded-sm"
                  />
                  {user?.pending_email && (
                    <p className="mt-2 text-xs text-neutral-500" data-testid="profile-email-pending">
                      {t("profile.emailPendingNote", { email: user.pending_email })}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  data-testid="profile-save-btn"
                  disabled={savingInfo}
                  className="rounded-sm bg-neutral-950 hover:bg-neutral-800 text-white h-11 mt-4"
                >
                  {savingInfo ? t("profile.saving") : t("common.save")}
                </Button>
              </form>
            )}

            {tab === "account" && (
              <div className="max-w-md mt-10 pt-8 border-t border-neutral-200" data-testid="profile-ai-credit">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mb-2">
                  {t("profile.aiCreditLabel")}
                </div>
                <div className="font-display text-2xl font-black tracking-tighter flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#E05236]" />
                  {t("profile.aiCreditTitle")}
                </div>
                {aiUsageError && !aiUsage ? (
                  <p className="text-sm text-neutral-600 mt-3">{t("profile.aiCreditError")}</p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      {[
                        ["aiCreditMonth", aiUsage?.current_month, "profile-ai-credit-month"],
                        ["aiCreditTotal", aiUsage?.total, "profile-ai-credit-total"],
                      ].map(([label, bucket, testId]) => (
                        <div key={label} className="border border-neutral-200 rounded-sm p-4">
                          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">{t(`profile.${label}`)}</div>
                          <div data-testid={testId} className="font-display text-2xl font-black tracking-tighter mt-1">
                            {aiUsage ? formatEur(bucket?.credit_eur) : "—"}
                          </div>
                          <div className="text-xs text-neutral-500 mt-1">
                            {t("profile.aiCreditCalls", { count: bucket?.calls || 0 })}
                          </div>
                        </div>
                      ))}
                    </div>
                    {aiUsage?.billing?.enabled && (
                      <div className="mt-4 border border-neutral-200 rounded-sm divide-y divide-neutral-200" data-testid="profile-ai-billing">
                        {[
                          ["aiBillingPending", aiUsage.billing.pending, "profile-ai-billing-pending"],
                          ["aiBillingInvoiced", aiUsage.billing.invoiced, "profile-ai-billing-invoiced"],
                          ["aiBillingBilled", aiUsage.billing.billed, "profile-ai-billing-billed"],
                          ["aiBillingNotBilled", aiUsage.billing.not_billed, "profile-ai-billing-not-billed"],
                        ].map(([label, bucket, testId]) => (
                          <div key={label} className="flex items-baseline justify-between px-4 py-2.5">
                            <div>
                              <div className="text-sm text-neutral-800">{t(`profile.${label}`)}</div>
                              <div className="text-xs text-neutral-500">
                                {t("profile.aiCreditCalls", { count: bucket?.calls || 0 })}
                              </div>
                            </div>
                            <div data-testid={testId} className="font-display text-lg font-black tracking-tighter">
                              {formatEur(bucket?.credit_eur)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {aiUsage?.billing?.enabled && (
                      <p className="text-xs text-neutral-500 mt-3">{t("profile.aiBillingDesc")}</p>
                    )}
                  </>
                )}
              </div>
            )}

            {tab === "branding" && (
              <form onSubmit={handleSaveBrand} className="space-y-5 max-w-md">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mb-2">
                    {t("profile.brandingLabel")}
                  </div>
                  <div className="font-display text-2xl font-black tracking-tighter">
                    {t("profile.brandingTitle")}
                  </div>
                  <p className="text-sm text-neutral-600 mt-2">
                    {t("profile.brandingDesc")}
                  </p>
                </div>

                {!user?.is_pro ? (
                  <div className="border border-stone-200 bg-stone-50 rounded-sm p-6 text-center">
                    <Sparkles className="w-6 h-6 mx-auto text-[#E05236] mb-3" />
                    <div className="font-display text-lg font-bold mb-2">
                      {t("profile.brandingProOnly")}
                    </div>
                    <p className="text-sm text-neutral-600 mb-5">
                      {t("profile.brandingProDesc")}
                    </p>
                    <Button
                      type="button"
                      data-testid="branding-upgrade-btn"
                      onClick={() => {
                        onOpenChange(false);
                        onUpgradeClick?.();
                      }}
                      className="rounded-sm bg-[#E05236] hover:bg-[#C94A31] text-white h-11 px-6"
                    >
                      {t("profile.upgradeCta")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em]">
                        {t("profile.companyName")}
                      </Label>
                      <Input
                        data-testid="branding-company-input"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder={t("profile.companyPlaceholder")}
                        className="mt-2 h-11 rounded-sm"
                      />
                    </div>

                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em]">
                        {t("profile.logo")}
                      </Label>
                      <div className="mt-2 border border-stone-200 rounded-sm p-4 flex items-center gap-4">
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt="Logo"
                            data-testid="branding-logo-preview"
                            className="w-20 h-20 rounded-sm object-contain bg-stone-50 border border-stone-200"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-sm bg-stone-100 border border-dashed border-stone-300 grid place-items-center text-neutral-400">
                            <ImageIcon className="w-6 h-6" strokeWidth={1.5} />
                          </div>
                        )}
                        <div className="flex-1 space-y-2">
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            style={{ display: "none" }}
                            data-testid="branding-logo-input"
                            onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                          />
                          <Button
                            type="button"
                            data-testid="branding-logo-btn"
                            onClick={() => logoInputRef.current?.click()}
                            variant="outline"
                            disabled={uploadingLogo}
                            className="rounded-sm h-10"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {uploadingLogo ? t("profile.uploading") : logoUrl ? t("profile.replaceLogo") : t("profile.uploadLogo")}
                          </Button>
                          {logoUrl && (
                            <Button
                              type="button"
                              data-testid="branding-logo-remove"
                              onClick={handleRemoveLogo}
                              variant="outline"
                              className="rounded-sm h-10 border-red-300 text-red-600 hover:bg-red-50 ml-2"
                            >
                              <X className="w-4 h-4 mr-2" />
                              {t("profile.removeLogo")}
                            </Button>
                          )}
                          <p className="text-[10px] text-neutral-500">
                            {t("profile.logoHint")}
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      data-testid="branding-save-btn"
                      disabled={savingBrand}
                      className="rounded-sm bg-neutral-950 hover:bg-neutral-800 text-white h-11 mt-2"
                    >
                      {savingBrand ? t("profile.saving") : t("common.save")}
                    </Button>
                  </>
                )}
              </form>
            )}

            {tab === "password" && (
              <form onSubmit={handleChangePwd} className="space-y-4 max-w-md">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mb-2">
                    {t("profile.security")}
                  </div>
                  <div className="font-display text-2xl font-black tracking-tighter">
                    {t("profile.changePwd")}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em]">
                    {t("profile.currentPwd")}
                  </Label>
                  <Input
                    type="password"
                    data-testid="profile-current-pwd"
                    autoComplete="current-password"
                    value={currentPwd}
                    onChange={(e) => setCurrentPwd(e.target.value)}
                    placeholder={t("profile.currentPwdPlaceholder")}
                    className="mt-2 h-11 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em]">
                    {t("profile.newPwd")}
                  </Label>
                  <Input
                    type="password"
                    data-testid="profile-new-pwd"
                    autoComplete="new-password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    required
                    minLength={12}
                    className="mt-2 h-11 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em]">
                    {t("profile.confirmPwd")}
                  </Label>
                  <Input
                    type="password"
                    data-testid="profile-confirm-pwd"
                    autoComplete="new-password"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    required
                    minLength={12}
                    className="mt-2 h-11 rounded-sm"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="profile-change-pwd-btn"
                  disabled={changingPwd}
                  className="rounded-sm bg-neutral-950 hover:bg-neutral-800 text-white h-11 mt-4"
                >
                  {changingPwd ? t("profile.updating") : t("profile.update")}
                </Button>
                <div className="border-t border-stone-200 pt-6 mt-8">
                  <div className="font-display text-lg font-black tracking-tighter">
                    {t("profile.logoutAllTitle")}
                  </div>
                  <p className="text-sm text-neutral-600 mt-1">{t("profile.logoutAllHint")}</p>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="profile-logout-all-btn"
                    disabled={loggingOutAll}
                    onClick={handleLogoutAll}
                    className="rounded-sm h-11 mt-4"
                  >
                    {t("profile.logoutAllBtn")}
                  </Button>
                </div>
              </form>
            )}

            {tab === "billing" && (
              <div className="space-y-6 max-w-2xl">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mb-2">
                    {t("profile.subscription")}
                  </div>
                  <div className="font-display text-2xl font-black tracking-tighter">
                    {t("profile.currentPlan")}
                  </div>
                </div>
                <div className="border border-stone-200 rounded-sm p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm font-semibold ${
                        user?.is_pro
                          ? "bg-[#E05236] text-white"
                          : "bg-stone-100 text-neutral-600"
                      }`}
                    >
                      {user?.is_pro ? "Pro" : "Free"}
                    </div>
                  </div>
                  <div className="font-display font-black text-3xl tracking-tighter">
                    {user?.is_pro ? t("profile.priceProMonth") : "0€"}
                  </div>
                  <div className="text-sm text-neutral-600 mt-1">
                    {user?.is_pro ? t("profile.perksPro") : t("profile.perksFree")}
                  </div>
                  {!user?.is_pro && (
                    <Button
                      data-testid="profile-upgrade-btn"
                      onClick={() => {
                        onOpenChange(false);
                        onUpgradeClick?.();
                      }}
                      className="w-full mt-6 rounded-sm bg-[#E05236] hover:bg-[#C94A31] text-white h-11"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t("profile.upgradeCta")}
                    </Button>
                  )}
                  {user?.is_pro && (
                    <div className="mt-6 space-y-4">
                      {/* Subscription status block */}
                      {loadingSub && (
                        <div className="text-xs text-neutral-500">
                          {(t("common.loading") || "Chargement") + "…"}
                        </div>
                      )}

                      {sub?.cancel_at_period_end ? (
                        <div
                          data-testid="sub-cancel-scheduled"
                          className="border border-amber-200 bg-amber-50 rounded-sm p-4"
                        >
                          <div className="flex items-start gap-2">
                            <CalendarClock className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
                            <div className="space-y-1">
                              <div className="text-sm font-semibold text-amber-900">
                                {t("profile.cancelScheduledTitle")}
                              </div>
                              <div className="text-xs text-amber-800">
                                {t("profile.cancelScheduledDesc", {
                                  date: formatSubDate(sub.current_period_end_iso),
                                })}
                              </div>
                            </div>
                          </div>
                          <Button
                            data-testid="sub-reactivate-btn"
                            onClick={handleReactivate}
                            disabled={reactivating}
                            className="w-full mt-3 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-10"
                          >
                            {reactivating
                              ? t("profile.reactivating")
                              : t("profile.reactivateBtn")}
                          </Button>
                        </div>
                      ) : (
                        <>
                          {sub?.current_period_end_iso && (
                            <div className="text-xs text-neutral-500 flex items-center gap-1.5">
                              <CalendarClock className="w-3.5 h-3.5" />
                              {t("profile.renewsOn", {
                                date: formatSubDate(sub.current_period_end_iso),
                              })}
                            </div>
                          )}
                          <p className="text-xs text-neutral-500">
                            {t("profile.cancelHint")}
                          </p>
                          <Button
                            data-testid="sub-cancel-btn"
                            variant="outline"
                            onClick={() => setCancelOpen(true)}
                            className="w-full rounded-sm border-red-300 text-red-600 hover:bg-red-50 h-10"
                          >
                            {t("profile.cancelBtn")}
                          </Button>
                        </>
                      )}

                      {/* Stripe Customer Portal — payment methods, billing address, plan */}
                      <Button
                        data-testid="sub-portal-btn"
                        variant="outline"
                        onClick={handleOpenPortal}
                        disabled={openingPortal}
                        className="w-full rounded-sm border-stone-300 hover:bg-stone-50 h-10"
                      >
                        <Settings className="w-3.5 h-3.5 mr-2" />
                        {openingPortal
                          ? t("profile.portalOpening")
                          : t("profile.portalBtn")}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Invoice history — visible only for pro users */}
                {user?.is_pro && (
                  <div data-testid="billing-invoices">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4 text-[#E05236]" />
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold">
                        {t("profile.invoicesTitle")}
                      </div>
                    </div>
                    {loadingInvoices ? (
                      <div className="text-xs text-neutral-500">
                        {(t("common.loading") || "Chargement") + "…"}
                      </div>
                    ) : invoices.length === 0 ? (
                      <div className="border border-dashed border-stone-300 rounded-sm p-6 text-center text-xs text-neutral-500">
                        {t("profile.invoicesEmpty")}
                      </div>
                    ) : (
                      <div className="border border-stone-200 rounded-sm overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-stone-50 border-b border-stone-200 text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">
                            <tr>
                              <th className="p-3 text-left">Date</th>
                              <th className="p-3 text-left">N°</th>
                              <th className="p-3 text-right">Montant</th>
                              <th className="p-3 text-left">Statut</th>
                              <th className="p-3 text-right w-24"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map((inv) => (
                              <tr
                                key={inv.id}
                                data-testid={`invoice-row-${inv.id}`}
                                className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50 transition-colors"
                              >
                                <td className="p-3 font-mono-plex text-xs text-neutral-700">
                                  {formatSubDate(inv.created_iso)}
                                </td>
                                <td className="p-3 font-mono-plex text-xs text-neutral-500">
                                  {inv.number || inv.id?.slice(0, 12)}
                                </td>
                                <td className="p-3 text-right font-mono-plex text-xs font-semibold">
                                  {(((inv.amount_paid || inv.amount_due) / 100) || 0).toLocaleString(
                                    i18n.language || undefined,
                                    { style: "currency", currency: inv.currency || "EUR" }
                                  )}
                                </td>
                                <td className="p-3">
                                  <span
                                    className={`inline-block px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-[0.1em] font-semibold ${
                                      inv.status === "paid"
                                        ? "bg-emerald-50 text-emerald-700"
                                        : inv.status === "open"
                                          ? "bg-amber-50 text-amber-700"
                                          : inv.status === "void"
                                            ? "bg-stone-100 text-neutral-600"
                                            : inv.status === "uncollectible"
                                              ? "bg-red-50 text-red-700"
                                              : "bg-stone-100 text-neutral-600"
                                    }`}
                                  >
                                    {t(
                                      `profile.invoiceStatus${
                                        (inv.status || "draft").charAt(0).toUpperCase() +
                                        (inv.status || "draft").slice(1)
                                      }`,
                                      inv.status || "—"
                                    )}
                                  </span>
                                </td>
                                <td className="p-3 text-right">
                                  <div className="inline-flex items-center gap-1">
                                    {inv.invoice_pdf && (
                                      <a
                                        data-testid={`invoice-pdf-${inv.id}`}
                                        href={inv.invoice_pdf}
                                        target="_blank"
                                        rel="noreferrer"
                                        title={t("profile.invoicePdfDownload")}
                                        className="p-1.5 rounded-sm hover:bg-stone-100 text-neutral-600 hover:text-[#E05236] transition-colors"
                                      >
                                        <Download className="w-3.5 h-3.5" />
                                      </a>
                                    )}
                                    {inv.hosted_invoice_url && (
                                      <a
                                        data-testid={`invoice-view-${inv.id}`}
                                        href={inv.hosted_invoice_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        title={t("profile.invoiceViewOnline")}
                                        className="p-1.5 rounded-sm hover:bg-stone-100 text-neutral-600 hover:text-[#E05236] transition-colors"
                                      >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                      </a>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === "privacy" && (
              <div className="space-y-6 max-w-xl">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mb-2">
                    {t("gdpr.eyebrow")}
                  </div>
                  <div className="font-display text-2xl font-black tracking-tighter">
                    {t("gdpr.title")}
                  </div>
                  <p className="text-sm text-neutral-600 mt-2">{t("gdpr.intro")}</p>
                </div>
                <div className="border border-stone-200 rounded-sm p-5">
                  <div className="font-semibold text-sm mb-1">{t("gdpr.exportTitle")}</div>
                  <p className="text-xs text-neutral-500 mb-4">{t("gdpr.exportDesc")}</p>
                  <Button
                    data-testid="gdpr-export-btn"
                    onClick={handleExportGdpr}
                    disabled={exporting}
                    className="rounded-sm bg-neutral-950 hover:bg-black text-white h-10"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exporting ? t("gdpr.exporting") : t("gdpr.exportBtn")}
                  </Button>
                </div>
                <div className="border border-stone-200 rounded-sm p-5">
                  <div className="font-semibold text-sm mb-1">{t("gdpr.consentTitle")}</div>
                  <p className="text-xs text-neutral-500">{t("gdpr.consentDesc")}</p>
                </div>
                <div className="border border-red-200 bg-red-50 rounded-sm p-5">
                  <div className="font-semibold text-sm mb-1 text-red-800">{t("gdpr.deleteTitle")}</div>
                  <p className="text-xs text-red-700 mb-3">{t("gdpr.deleteDesc")}</p>
                  <Button
                    variant="outline"
                    onClick={() => setTab("danger")}
                    data-testid="gdpr-goto-danger"
                    className="rounded-sm border-red-300 text-red-700 hover:bg-red-100 h-9"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t("gdpr.deleteTitle")}
                  </Button>
                </div>
                <div className="text-xs text-neutral-500">
                  <a
                    href="/legal/confidentialite"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#E05236] hover:underline"
                  >
                    {t("gdpr.privacyLinks")}
                  </a>
                </div>
              </div>
            )}

            {tab === "danger" && (
              <form onSubmit={handleCloseAccount} className="space-y-4 max-w-md">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-semibold mb-2">
                    {t("profile.dangerZone")}
                  </div>
                  <div className="font-display text-2xl font-black tracking-tighter">
                    {t("profile.closeAccount")}
                  </div>
                </div>
                <div className="border border-red-200 bg-red-50 rounded-sm p-4 text-sm text-red-800">
                  {t("profile.closeWarning")}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em]">
                    {t("profile.pwdIfEmail")}
                  </Label>
                  <Input
                    type="password"
                    data-testid="profile-close-pwd"
                    autoComplete="current-password"
                    value={closePwd}
                    onChange={(e) => setClosePwd(e.target.value)}
                    placeholder={t("profile.pwdRequiredEmail")}
                    className="mt-2 h-11 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em]">
                    {t("profile.typeDelete", { keyword: DELETE_KEYWORD })}
                  </Label>
                  <Input
                    data-testid="profile-close-confirm"
                    value={closeConfirm}
                    onChange={(e) => setCloseConfirm(e.target.value)}
                    placeholder={DELETE_KEYWORD}
                    required
                    className="mt-2 h-11 rounded-sm border-red-300 focus-visible:ring-red-500"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="profile-close-btn"
                  disabled={closing || closeConfirm !== DELETE_KEYWORD}
                  className="rounded-sm bg-red-600 hover:bg-red-700 text-white h-11 mt-2"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {closing ? t("profile.deleting") : t("profile.deleteForever")}
                </Button>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <CancelSubscriptionModal
      open={cancelOpen}
      onOpenChange={setCancelOpen}
      subscription={sub}
      onCancelled={(data) => setSub(data)}
    />
    </>
  );
}
